/** @jsxImportSource ../i18n */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Briefcase, Eye, RefreshCw, SearchX } from 'lucide-react';
import { fetchPortfolio, fetchWatchlist, fetchAnalysisExclusions, setAnalysisExclusion } from '../store/slices/portfolioSlice';
import { exclusionKey } from '../services/analysisExclusions';
import { useToast } from '../contexts/ToastContext';
import Watchlist from '../components/Watchlist';
import { BrokerGate } from '../components/shell/Broker';
import { cx } from '../theme/cx';
import { formatNumber, formatPercent } from '../theme/format';
import {
  Badge, Banner, Button, Card, Checkbox, Delta, EmptyState, Money, PageHeader, Percent, SearchInput, SegmentedControl,
  SkeletonTable, Stat, SymbolMark, Table, Tabs, TBody, Td, Th, THead, Tr,
} from '../components/ui';

const FILTERS = [
  { value: 'all', label: 'Toutes' },
  { value: 'profit', label: 'En gain' },
  { value: 'loss', label: 'En perte' },
];

const COLUMNS = [
  { key: 'symbol', label: 'Valeur' },
  { key: 'amount', label: 'Quantité', align: 'right' },
  { key: 'open_price', label: 'PRU', align: 'right', className: 'hidden xl:table-cell' },
  { key: 'current_price', label: 'Cours', align: 'right', className: 'hidden xl:table-cell' },
  { key: 'market_value', label: 'Valorisation', align: 'right' },
  { key: 'weight', label: 'Poids', align: 'right', className: 'hidden lg:table-cell', sortable: false },
  { key: 'profit_loss_pct', label: '+/- value', align: 'right' },
];

const quantity = (value) => formatNumber(value, { decimals: Number.isInteger(Number(value)) ? 0 : 2 });

const stockPath = (symbol) => `/stock/${encodeURIComponent(symbol)}`;

const compare = (key, direction) => (a, b) => {
  const aVal = a[key];
  const bVal = b[key];
  if (typeof aVal === 'string' && typeof bVal === 'string') {
    return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  }
  return direction === 'asc' ? (aVal || 0) - (bVal || 0) : (bVal || 0) - (aVal || 0);
};

const WeightBar = ({ value }) => (
  <span className="inline-flex items-center justify-end gap-3">
    <span aria-hidden="true" className="hidden h-1 w-14 overflow-hidden rounded-full bg-surface-3 min-[1400px]:block">
      <span className="block h-full rounded-full bg-gold-500/70" style={{ width: `${Math.min(value, 100)}%` }} />
    </span>
    <span className="num">{formatPercent(value, { sign: false })}</span>
  </span>
);

const PositionsPanel = ({ positions, summary, loading, isExcluded, exclusionToggle, exclusionsError }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'market_value', direction: 'desc' });
  const total = summary?.total_value || 0;
  const weight = (p) => (total > 0 ? ((p.market_value || 0) / total) * 100 : 0);

  const visible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return positions
      .filter((p) => !term || p.symbol?.toLowerCase().includes(term) || p.description?.toLowerCase().includes(term))
      .filter((p) => (filter === 'profit' ? p.profit_loss >= 0 : filter === 'loss' ? p.profit_loss < 0 : true))
      .sort(compare(sort.key, sort.direction));
  }, [positions, searchTerm, filter, sort]);

  const excludedCount = positions.filter(isExcluded).length;
  const onSort = (key) => setSort((current) => (current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: key === 'symbol' ? 'asc' : 'desc' }));

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Stat size="compact" label="Valorisation" loading={!summary && loading} value={<Money value={summary?.total_value} decimals={0} />} hint={summary && <>dont <Money value={summary.cash_balance} decimals={0} /> de liquidités</>} />
        <Stat size="compact" label="+/- value latente" loading={!summary && loading} value={<Money value={summary?.total_profit_loss} decimals={0} sign colorize />} />
        <Stat size="compact" label="Rendement" loading={!summary && loading} value={<Percent value={summary?.total_profit_loss_pct} colorize />} />
        <Stat size="compact" label="Positions" loading={!summary && loading} value={<span className="num">{positions.length}</span>} hint={excludedCount > 0 ? `${excludedCount} exclue(s) des rapports` : null} />
      </div>

      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm('')}
            placeholder="Rechercher une valeur…"
            aria-label="Rechercher une valeur"
            className="w-full sm:max-w-xs"
          />
          <SegmentedControl ariaLabel="Filtrer les positions" options={FILTERS} value={filter} onChange={setFilter} />
        </div>
        {(excludedCount > 0 || exclusionsError) && (
          <div className="mt-4 space-y-2">
            {excludedCount > 0 && (
              <Banner tone="warn" compact>
                {excludedCount} ligne(s) exclue(s) des rapports quotidiens — elles restent comptées dans les totaux.
              </Banner>
            )}
            {exclusionsError && <Banner tone="loss" compact role="alert">{exclusionsError}</Banner>}
          </div>
        )}

        <div className="mt-5">
          {loading && positions.length === 0 ? (
            <div role="status">
              <span className="sr-only">Chargement du portefeuille…</span>
              <SkeletonTable rows={6} cols={5} />
            </div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon={positions.length ? SearchX : Briefcase}
              title={positions.length ? 'Aucune position ne correspond à vos critères' : 'Aucune position'}
              description={positions.length ? 'Modifiez la recherche ou le filtre pour afficher d’autres lignes.' : 'Votre portefeuille ne contient pas encore de ligne.'}
            />
          ) : (
            <>
              <ul className="space-y-3 md:hidden">
                {visible.map((p) => (
                  <li key={p.position_id} className={cx('rounded-control border border-line bg-surface-2/50 p-4', isExcluded(p) && 'opacity-50')}>
                    <div className="flex items-start justify-between gap-3">
                      <Link to={stockPath(p.symbol)} className="flex min-w-0 items-center gap-3">
                        <SymbolMark symbol={p.symbol} />
                        <span className="min-w-0">
                          <span className="flex items-center gap-2">
                            <span className="truncate font-medium text-fg">{p.symbol}</span>
                            {isExcluded(p) && <Badge tone="warn">Exclu</Badge>}
                          </span>
                          <span className="block truncate text-xs text-fg-subtle">{p.description}</span>
                        </span>
                      </Link>
                      <Delta value={p.profit_loss_pct} />
                    </div>
                    <dl className="mt-4 grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <dt className="text-xs text-fg-subtle">Valorisation</dt>
                        <dd className="mt-1 font-medium text-fg"><Money value={p.market_value} decimals={0} /></dd>
                      </div>
                      <div>
                        <dt className="text-xs text-fg-subtle">Quantité</dt>
                        <dd className="num mt-1 text-fg-body">{quantity(p.amount)}</dd>
                      </div>
                      <div className="text-right">
                        <dt className="text-xs text-fg-subtle">+/- value</dt>
                        <dd className="mt-1"><Money value={p.profit_loss} decimals={0} sign colorize /></dd>
                      </div>
                    </dl>
                    <label className="mt-4 flex cursor-pointer items-center gap-3 border-t border-line pt-3 text-xs text-fg-muted">
                      {exclusionToggle(p)}
                      Exclure de l'analyse (rapports quotidiens)
                    </label>
                  </li>
                ))}
              </ul>

              <Table containerClassName="hidden md:block">
                <THead>
                  <tr>
                    {COLUMNS.map((col) => (
                      <Th
                        key={col.key}
                        align={col.align}
                        className={col.className}
                        sortable={col.sortable !== false}
                        sortDirection={sort.key === col.key ? sort.direction : undefined}
                        onSort={() => onSort(col.key)}
                      >
                        {col.label}
                      </Th>
                    ))}
                    <Th align="center" title="Exclure de l'analyse dans les rapports quotidiens">Exclure</Th>
                  </tr>
                </THead>
                <TBody>
                  {visible.map((p) => {
                    const excluded = isExcluded(p);
                    return (
                      <Tr key={p.position_id} className={excluded ? 'opacity-50' : undefined}>
                        <Td className="w-full max-w-0">
                          <div className="flex items-center gap-3">
                            <SymbolMark symbol={p.symbol} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Link to={stockPath(p.symbol)} className="truncate font-medium text-fg transition-colors hover:text-gold-200">{p.symbol}</Link>
                                {excluded && <Badge tone="warn">Exclu</Badge>}
                              </div>
                              <p className="truncate text-xs text-fg-subtle">{p.description}</p>
                            </div>
                          </div>
                        </Td>
                        <Td align="right" numeric>{quantity(p.amount)}</Td>
                        <Td align="right" className="hidden xl:table-cell"><Money value={p.open_price} sensitive={false} currency={p.currency || 'EUR'} /></Td>
                        <Td align="right" className="hidden xl:table-cell"><Money value={p.current_price} sensitive={false} currency={p.currency || 'EUR'} /></Td>
                        <Td align="right"><Money value={p.market_value} decimals={0} className="font-medium text-fg" /></Td>
                        <Td align="right" className="hidden lg:table-cell"><WeightBar value={weight(p)} /></Td>
                        <Td align="right">
                          <span className="flex flex-col items-end gap-1">
                            <Delta value={p.profit_loss_pct} />
                            <Money value={p.profit_loss} decimals={0} sign colorize className="text-xs" />
                          </span>
                        </Td>
                        <Td align="center">{exclusionToggle(p)}</Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </>
          )}
        </div>
      </Card>
    </>
  );
};

const Portfolio = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { positions, summary, saxoConnected, loading, watchlist, analysisExclusions, analysisExclusionsError, exclusionPending } = useSelector((state) => state.portfolio);
  const [activeTab, setActiveTab] = useState('positions');

  useEffect(() => {
    if (saxoConnected) {
      dispatch(fetchPortfolio());
    }
  }, [dispatch, saxoConnected]);

  useEffect(() => { dispatch(fetchWatchlist()); }, [dispatch]);

  useEffect(() => { dispatch(fetchAnalysisExclusions()); }, [dispatch]);

  const excludedKeys = new Set(analysisExclusions.map((e) => e.key));
  const isExcluded = (position) => excludedKeys.has(exclusionKey(position.symbol));
  const toggleExclusion = async (position) => {
    const excluded = !isExcluded(position);
    try {
      await dispatch(setAnalysisExclusion({ symbol: position.symbol, excluded })).unwrap();
      toast.success(excluded ? `${position.symbol} exclu des rapports quotidiens` : `${position.symbol} réintégré aux rapports quotidiens`);
    } catch {
      // The error stays inline, next to the table (analysisExclusionsError).
    }
  };
  const exclusionToggle = (position) => {
    const excluded = isExcluded(position);
    return (
      <Checkbox
        switch
        checked={excluded}
        disabled={!!exclusionPending[position.symbol]}
        onChange={() => toggleExclusion(position)}
        aria-label={excluded ? `Ré-inclure ${position.symbol} dans l'analyse` : `Exclure ${position.symbol} de l'analyse`}
      />
    );
  };

  const tabs = [
    { id: 'positions', label: 'Positions', icon: Briefcase, tabId: 'portfolio-tab-positions', panelId: 'portfolio-panel-positions' },
    { id: 'watchlist', label: `Watchlist (${watchlist.length})`, icon: Eye, tabId: 'portfolio-tab-watchlist', panelId: 'portfolio-panel-watchlist' },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Patrimoine"
        title="Portefeuille"
        description="Vos lignes, leur poids dans le PEA et les valeurs que vous surveillez."
        actions={activeTab === 'positions' && saxoConnected && (
          <Button variant="secondary" leadingIcon={RefreshCw} loading={loading} onClick={() => dispatch(fetchPortfolio())}>
            Actualiser
          </Button>
        )}
      />

      <div className="space-y-6">
        <Tabs ariaLabel="Vue du portefeuille" items={tabs} value={activeTab} onChange={setActiveTab} />
        <div role="tabpanel" id={`portfolio-panel-${activeTab}`} aria-labelledby={`portfolio-tab-${activeTab}`} className="space-y-6">
          {activeTab === 'watchlist' ? <Watchlist /> : (
            <BrokerGate>
              <PositionsPanel
                positions={positions}
                summary={summary}
                loading={loading}
                isExcluded={isExcluded}
                exclusionToggle={exclusionToggle}
                exclusionsError={analysisExclusionsError}
              />
            </BrokerGate>
          )}
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
